<template>
  <div class="md-layout-item">
    <div class="isis-page__header">
      <div class="nav-link" @click="$router.back()">
        <i class="fas fa-chevron-left"></i>
        <span>{{ fullName }} transcript</span>
      </div>
    </div>

    <md-card>
      <md-card-content>
        <div class="md-layout">
          <div class="md-layout-item md-size-20 md-small-size-100">
            <md-field>
              <label>Template</label>
              <md-select
                id="template"
                v-model="template"
                name="template"
                required
              >
                <md-option value="elementary school"
                  >Elementary school
                </md-option>
                <md-option value="middle school">Middle school</md-option>
                <md-option value="high school">High school</md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-30 md-small-size-100">
            <md-field>
              <label>{{ $t("common.program") }}</label>
              <md-select
                id="programs"
                v-model="programSelected"
                :disabled="!template"
                multiple
                name="programs"
                required
              >
                <md-option
                  v-for="(program, index) in programs"
                  :key="program.name + index"
                  :value="program.id"
                  >{{ program.name }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div
            v-if="programSelected.length"
            class="md-layout-item md-size-10 md-small-size-100 text-center"
          >
            <md-button class="md-warning" @click="getRequest('unofficial')">
              View
            </md-button>
          </div>

          <div v-if="pdfSrc" class="md-size-40 md-small-size-100 text-center">
            <a :download="fileName" :href="pdfSrc">
              <md-button class="md-primary">
                <i class="fas fa-file-download mr-1"></i> Unofficial
              </md-button>
            </a>

            <md-button
              v-if="hasValidRole(role, [Role.Admin, Role.Principal])"
              class="md-success ml-2"
              @click="getRequest('official')"
            >
              <i class="fas fa-file-download mr-1"></i> Official
            </md-button>
          </div>
        </div>

        <div class="md-layout">
          <div v-if="pdfSrc" class="md-layout-item md-size-100 bg-[#525659]">
            <div class="pdf-view">
              <iframe :src="pdfSrc" style="width: 100%; height: 100%" />
            </div>
          </div>
          <div v-else class="md-layout-item md-size-100">
            Please select a template and program(s) to view student transcripts!
          </div>
        </div>

        <DefaultTranscript
          v-if="template === 'default'"
          :programId="programSelected"
        ></DefaultTranscript>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import { service_names, service_paths } from "@/constants/Services";
import Role from "@/constants/Role";
import { hasValidRole, isDynamicRole } from "@/helpers/role-helper";
import { mapGetters, mapState } from "vuex";
import { forbidden_router_name } from "@/routes/routes";
import { Student } from "@/constants/Permissions";
import { passesIgscTranscriptRule } from "@/helpers/permission";
import DefaultTranscript from "./DefaultTranscript";
import Swal from "sweetalert2";
import AWSConst from "@/constants/AWS";
import { postFileToS3, saveUploadedLink } from "@/helpers/aws-s3";
import moment from "moment";
import {
  downloadByUrl,
  generatePdfSrc,
  getId,
  initLoader,
  Message
} from "../../../helpers/utils";

export default {
  components: {
    DefaultTranscript
  },
  data() {
    const studentId = this.$route.params.id;
    return {
      hasValidRole,
      Role,
      Student,
      isDynamicRole,
      fullName: "",
      studentId,
      programs: [],
      programSelected: [],
      template: "",
      type: "",
      pdfSrc: null
    };
  },
  watch: {
    template(newValue) {
      if (newValue) {
        this.programs = [];
        this.programSelected = [];
        this.pdfSrc = null;
        this.getAllPrograms();
      }
    }
  },
  computed: {
    ...mapGetters("user", ["role"]),
    ...mapState("user", ["info"]),

    verifyUser() {
      // IGSC restricts transcripts to the Admin role, whoever the student is
      if (!passesIgscTranscriptRule(this.role)) return false;

      return (
        getId(this.info) === this.studentId ||
        hasValidRole(this.role, [Role.Admin, Role.Principal, Role.Family]) ||
        (isDynamicRole(this.info, this.role) &&
          this.$can(Student.actions.viewTranscript, Student.subject))
      );
    },

    fileName() {
      const timeExport = moment().format("YYYY-MM-DD HH:mm:ss");
      return `${this.fullName}_${timeExport}`;
    }
  },
  methods: {
    getStudentById() {
      this.$axios
        .get(`${service_paths.students}/${this.studentId}`)
        .then(({ data }) => {
          this.fullName = data.full_name;
        });
    },

    getAllPrograms() {
      this.$axios
        .get(`${service_paths.programs}`, {
          user_id: this.studentId,
          transcript_template: this.template
        })
        .then(({ data }) => {
          if (data.data.length) {
            this.programs = data.data;
          } else {
            Message.error(this.$t("program.program_not_exist"));
          }
        });
    },

    getRequest(type) {
      this.type = type;
      if (type === "official") {
        Swal.fire({
          title: this.$t("common.are_you_sure"),
          text: this.$t("transcript_pdf.save_log_official"),
          type: "warning",
          showCancelButton: true,
          confirmButtonClass: "md-button md-success btn-fill",
          cancelButtonClass: "md-button md-danger btn-fill",
          confirmButtonText: this.$t("common.ok"),
          buttonsStyling: false
        }).then(result => {
          if (result.value) {
            this.getTranscript(type).then(res => {
              this.uploadFileLog(res.data);
            });
          }
        });
      } else {
        this.getTranscript(type).then(res => {
          this.pdfSrc = generatePdfSrc(res.data, {
            view: "fitH",
            scrollbar: 0,
            toolbar: 0,
            navpanes: 0
          });
        });
      }
    },

    getTranscript(type) {
      let param = {
        user_id: this.$route.params.id,
        program_ids: this.programSelected,
        template: this.template,
        type: type
      };
      return this.$axios.get(
        `${service_paths.export}/transcript-academic`,
        param,
        true,
        {},
        "blob"
      );
    },

    async uploadFileLog(data) {
      const loader = initLoader();

      const fileConvertToPdf = new File([data], `${this.fileName}.pdf`, {
        type: "application/pdf"
      });
      const s3PathUploadTranscript = `${AWSConst.S3_STUDENTS_UPLOAD_PATH}/transcript`;

      try {
        const linkFileS3 = await postFileToS3(
          s3PathUploadTranscript,
          fileConvertToPdf
        );

        // TODO: remove link_file
        await saveUploadedLink(
          linkFileS3,
          `${service_names.utility}/transcript-history`,
          { student_id: this.$route.params.id, link_file: linkFileS3 }
        );

        const url = window.URL.createObjectURL(data);
        downloadByUrl(url, this.fileName);

        Message.success(this.$t("transcript_pdf.saved_log"));
      } finally {
        loader.hide();
      }
    }
  },
  created() {
    this.getStudentById();
    if (!this.verifyUser) {
      this.$router.push({ name: forbidden_router_name });
    }
  }
};
</script>
<style>
.pdf-view {
  width: 720px;
  height: 1000px;
  margin: 0 auto;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.pdf-view iframe::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.pdf-view iframe {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
